import _ from 'lodash';
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';

import {
  containerGroupApiVersion,
  Subscription,
  ContainerGroup,
} from '../types';

import { queryKeys } from './query-keys';
import { buildContainerGroupUrl } from './utils';

export function useContainerGroups(
  environmentId: EnvironmentId,
  subscriptions: Subscription[] = [],
  enabled?: boolean,
  options: { refetchInterval?: number } = {}
) {
  const queries = useQueries({
    queries: useMemo(
      () =>
        subscriptions.map((subscription) => ({
          queryKey: queryKeys.containerGroups(
            environmentId,
            subscription.subscriptionId
          ),
          queryFn: async () =>
            getContainerGroups(environmentId, subscription.subscriptionId),
          ...withError('Unable to retrieve Azure container groups'),
          enabled,
          refetchInterval: options.refetchInterval,
        })),
      [subscriptions, enabled, environmentId, options.refetchInterval]
    ),
  });

  return useMemo(
    () => ({
      data: _.flatMap(_.compact(queries.map((q) => q.data))),
      isLoading: queries.some((q) => q.isLoading),
      isRefetching: queries.some((q) => q.isRefetching),
    }),
    [queries]
  );
}

export async function getContainerGroups(
  environmentId: EnvironmentId,
  subscriptionId: string
) {
  try {
    const { data } = await axios.get<{ value: ContainerGroup[] }>(
      buildContainerGroupUrl(environmentId, subscriptionId),
      {
        params: { 'api-version': containerGroupApiVersion },
      }
    );

    return data.value;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to retrieve container groups');
  }
}
